"use client"

import { useEffect, useState } from "react"
import { getSuspectFrame, formatFrame, isStackLikelyMapped, parseStack } from "@/lib/crash-locator"
import { persistCrashReport } from "@/components/error-capture"

function getBuildId(): string {
  if (typeof window === "undefined") return "ssr"
  const nextData = (window as unknown as { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__
  return nextData?.buildId ?? "unknown"
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [documentId, setDocumentId] = useState<string | null>(null)

  useEffect(() => {
    const stack = error.stack
    const suspect = getSuspectFrame(stack)
    const suspectFormatted = suspect ? formatFrame(suspect) : null
    const sourcemapWarn = !isStackLikelyMapped(stack)
    const buildId = getBuildId()

    console.error(
      "[CRASH LOCATOR] route error",
      "\n  message:",
      error.message,
      "\n  buildId:",
      buildId,
      "\n  suspect file:line:col:",
      suspectFormatted ?? "(none)",
      "\n  full stack:\n",
      stack ?? "(no stack)"
    )
    if (sourcemapWarn) {
      console.warn("[CRASH LOCATOR] Sourcemap may not be applied — check .map serving.")
    }

    if (process.env.NODE_ENV === "development") {
      const frames = parseStack(stack).slice(0, 10).map((f) => formatFrame(f))
      persistCrashReport({
        message: error.message,
        stack,
        route: typeof window !== "undefined" ? window.location.pathname : "",
        buildId,
        suspect: suspectFormatted,
        frames,
        userId: null,
      }).then((id) => id && setDocumentId(id))
    }
  }, [error])

  const suspect = getSuspectFrame(error.stack)
  const suspectFormatted = suspect ? formatFrame(suspect) : null

  return (
    <div style={{ fontFamily: "system-ui", padding: "2rem", maxWidth: "800px", margin: "0 auto" }}>
      <h2 style={{ color: "#dc2626" }}>Something went wrong</h2>
      <p><strong>Error:</strong> {error.message}</p>
      <p><strong>Suspect (file:line:col):</strong> <code style={{ background: "#f3f4f6", padding: "2px 6px" }}>{suspectFormatted ?? "(none)"}</code></p>
      {documentId && (
        <p><strong>Firestore doc:</strong> <code style={{ background: "#f3f4f6", padding: "2px 6px" }}>debug_crashes/{documentId}</code></p>
      )}
      <pre style={{ overflow: "auto", background: "#f3f4f6", padding: "1rem", fontSize: "12px" }}>
        {error.stack ?? "(no stack)"}
      </pre>
      <button
        type="button"
        onClick={() => reset()}
        className="rounded bg-primary px-4 py-2 text-primary-foreground"
      >
        Try again
      </button>
    </div>
  )
}
